/**
 * AI Agent Connector Config Generator
 *
 * Works like DogeShit Wallet — one-click setup for all major AI agents.
 * Generates config files, deep links, and copy-paste commands.
 *
 * When VITE_GAIA_MCP_URL is set (hosted mode), web agents use HTTP.
 * Otherwise CLI agents fall back to local stdio via npx.
 */

export type AgentConfig = {
  id: string;
  name: string;
  description: string;
  accent: string;
  icon: string;
  deepLink?: string;
  configFile: {
    filename: string;
    content: string;
  };
  steps: { title: string; body: string; code?: string }[];
};

const MCP_PACKAGE = "@gaia/mcp-mint";
const MCP_NAME = "gaia-mint";
const MCP_HOSTED = (import.meta.env.VITE_GAIA_MCP_URL ?? "") as string;
const MCP_REMOTE = (import.meta.env.VITE_GAIA_MCP_REMOTE_URL ?? "") as string;
const HAS_HOSTED = !!MCP_HOSTED && MCP_HOSTED.startsWith("https://");
const HAS_REMOTE = !!MCP_REMOTE && MCP_REMOTE.startsWith("https://");

function getLocalConfig() {
  return {
    name: MCP_NAME,
    command: "npx",
    args: ["-y", MCP_PACKAGE],
    env: {
      GAIA_ADDRESS_BASE: "0xD904F3dA69Cd2fC768E752d618c7C74aBE98D6C4",
      GAIA_DELEGATE_BASE: "0xB147331db2c6f894E7fAA5E50D6b7880438f833E",
      GAIA_DEFAULT_CHAIN: "base",
    },
  };
}

function getHostedConfig() {
  return {
    name: MCP_NAME,
    url: MCP_HOSTED,
  };
}

export const AGENT_CONNECTORS: AgentConfig[] = [
  {
    id: "claude-web",
    name: "Claude Web (claude.ai)",
    description: "Browser — no desktop app needed",
    accent: "from-[#cc7c5e] to-[#e8a87c]",
    icon: "◆",
    configFile: {
      filename: "claude_web_mcp.json",
      content: JSON.stringify(
        {
          mcpServers: {
            [MCP_NAME]: {
              url: HAS_REMOTE ? MCP_REMOTE : "https://gaia-mcp-remote.<YOUR_ACCOUNT>.workers.dev/mcp",
            },
          },
        },
        null,
        2
      ),
    },
    steps: [
      {
        title: "Remote MCP is live",
        body: "The Cloudflare Worker is deployed and Claude Web can reach it directly.",
        code: HAS_REMOTE ? MCP_REMOTE : "https://gaia-mcp-remote.arechampionw.workers.dev/mcp",
      },
      {
        title: "Open Claude Web Connectors",
        body: "Go to claude.ai → Customize (left sidebar) → Connectors → Add Custom Connector",
      },
      {
        title: "Paste the MCP URL",
        body: HAS_REMOTE
          ? `Paste this URL: ${MCP_REMOTE}`
          : "Paste your deployed Worker URL (e.g. https://gaia-mcp-remote.<account>.workers.dev/mcp)",
        code: HAS_REMOTE ? MCP_REMOTE : "",
      },
      {
        title: "Verify connection",
        body: "Claude will fetch the tool list and show: prepare_mint, get_status, get_user_mints, estimate_cost, submit_mint",
      },
      {
        title: "Start minting",
        body: 'Open a new chat and say: "Mint me some GAIA"',
      },
    ],
  },
  {
    id: "claude-desktop",
    name: "Claude Desktop",
    description: "macOS / Windows app",
    accent: "from-[#cc7c5e] to-[#e8a87c]",
    icon: "◆",
    deepLink: "claude://",
    configFile: {
      filename: "claude_desktop_config.json",
      content: JSON.stringify(
        {
          mcpServers: {
            [MCP_NAME]: HAS_HOSTED ? getHostedConfig() : getLocalConfig(),
          },
        },
        null,
        2
      ),
    },
    steps: [
      {
        title: "Download config",
        body: "Click the button below to download the Claude Desktop config file.",
      },
      {
        title: "Open Claude Desktop settings",
        body: "Claude Menu → Settings → Developer → Edit Config",
        code: "~/Library/Application Support/Claude/claude_desktop_config.json",
      },
      {
        title: "Paste the config",
        body: "Replace the contents with the downloaded file, then restart Claude.",
      },
      {
        title: "Start minting",
        body: 'Open a new chat and say: "Mint me some GAIA"',
      },
    ],
  },
  {
    id: "claude-code",
    name: "Claude Code (CLI)",
    description: "Terminal-based Claude",
    accent: "from-[#40b0c2] to-[#3ddc97]",
    icon: "$",
    configFile: {
      filename: "claude_code_setup.sh",
      content: `#!/bin/bash
# Claude Code MCP Setup for GAIA
set -e

echo "→ Installing GAIA MCP for Claude Code..."
claude mcp add ${MCP_NAME} -- npx -y ${MCP_PACKAGE}

echo "✓ Done! Start Claude Code and say: mint me some GAIA"
`,
    },
    steps: [
      {
        title: "Install Claude Code",
        body: "If not already installed:",
        code: "npm install -g @anthropics/claude-code",
      },
      {
        title: "Run setup",
        body: HAS_HOSTED
          ? `Your website has a live MCP server at ${MCP_HOSTED}. You can use either local (stdio) or hosted (HTTP).`
          : "Paste this command:",
        code: `claude mcp add ${MCP_NAME} -- npx -y ${MCP_PACKAGE}`,
      },
      {
        title: "Verify",
        body: "Check that the MCP is registered:",
        code: "claude mcp list",
      },
      {
        title: "Start minting",
        body: 'Run "claude" and say: "Mint me some GAIA"',
      },
    ],
  },
  {
    id: "codex",
    name: "Codex CLI (OpenAI)",
    description: "Terminal-based Codex",
    accent: "from-[#10a37f] to-[#1fd1a5]",
    icon: "⌥",
    configFile: {
      filename: "codex_config.toml",
      content: HAS_HOSTED
        ? `## Codex CLI MCP Config for GAIA (hosted)
## Place in ~/.codex/config.toml

[mcp_servers.${MCP_NAME}]
url = "${MCP_HOSTED}"
`
        : `## Codex CLI MCP Config for GAIA (local)
## Place in ~/.codex/config.toml

[mcp_servers.${MCP_NAME}]
command = "npx"
args = ["-y", "${MCP_PACKAGE}"]

[mcp_servers.${MCP_NAME}.env]
GAIA_ADDRESS_BASE = "0xD904F3dA69Cd2fC768E752d618c7C74aBE98D6C4"
GAIA_DELEGATE_BASE = "0xB147331db2c6f894E7fAA5E50D6b7880438f833E"
`,
    },
    steps: [
      {
        title: "Install Codex CLI",
        body: "If not already installed:",
        code: "npm install -g @openai/codex",
      },
      {
        title: "Download config",
        body: `Save the config file to ~/.codex/config.toml`,
      },
      {
        title: "Verify",
        body: "List MCP servers:",
        code: "codex mcp list",
      },
      {
        title: "Start minting",
        body: 'Run "codex" and say: "Use gaia-mint to mint GAIA"',
      },
    ],
  },
  {
    id: "cursor",
    name: "Cursor",
    description: "VS Code fork with AI",
    accent: "from-[#5b8bff] to-[#7ea8ff]",
    icon: "▶",
    configFile: {
      filename: "cursor_mcp.json",
      content: JSON.stringify(
        {
          mcpServers: {
            [MCP_NAME]: HAS_HOSTED ? getHostedConfig() : getLocalConfig(),
          },
        },
        null,
        2
      ),
    },
    steps: [
      {
        title: "Download config",
        body: "Click the button below to download the Cursor MCP config.",
      },
      {
        title: "Open Cursor settings",
        body: "Cursor → Settings → MCP",
        code: "~/.cursor/mcp.json",
      },
      {
        title: "Paste the config",
        body: HAS_HOSTED
          ? `Add the gaia-mint server (hosted at ${MCP_HOSTED}) to your mcpServers array.`
          : "Add the gaia-mint server to your mcpServers array.",
      },
      {
        title: "Restart Cursor",
        body: "Cmd/Ctrl + Shift + P → Reload Window",
      },
      {
        title: "Start minting",
        body: 'In chat: "Use the GAIA MCP to mint tokens"',
      },
    ],
  },
  {
    id: "cline",
    name: "Cline / Continue",
    description: "VS Code extension",
    accent: "from-[#9b6bff] to-[#c4a5ff]",
    icon: "</>",
    configFile: {
      filename: "cline_mcp.json",
      content: JSON.stringify(
        {
          mcpServers: {
            [MCP_NAME]: HAS_HOSTED ? getHostedConfig() : getLocalConfig(),
          },
        },
        null,
        2
      ),
    },
    steps: [
      {
        title: "Open VS Code settings",
        body: "Cline → Settings → MCP Servers",
      },
      {
        title: "Add server",
        body: HAS_HOSTED
          ? `Use hosted MCP: ${MCP_HOSTED}`
          : "Copy-paste this config:",
        code: JSON.stringify(HAS_HOSTED ? getHostedConfig() : getLocalConfig(), null, 2),
      },
      {
        title: "Start minting",
        body: 'In Cline chat: "Mint GAIA tokens for me"',
      },
    ],
  },
  {
    id: "aider",
    name: "Aider",
    description: "CLI pair programming",
    accent: "from-[#f5c451] to-[#ff8a5c]",
    icon: "A",
    configFile: {
      filename: "aider_setup.sh",
      content: `#!/bin/bash
# Aider MCP Setup for GAIA
set -e

echo "→ Installing GAIA MCP for Aider..."

# Add to your shell profile (.bashrc, .zshrc, etc.):
cat << 'EOF'

# GAIA MCP
export MCP_SERVER_GAIA_MINT_COMMAND="npx"
export MCP_SERVER_GAIA_MINT_ARGS="-y,${MCP_PACKAGE}"
export GAIA_ADDRESS_BASE="0xD904F3dA69Cd2fC768E752d618c7C74aBE98D6C4"
export GAIA_DELEGATE_BASE="0xB147331db2c6f894E7fAA5E50D6b7880438f833E"

EOF

echo "✓ Add the above exports to your ~/.bashrc or ~/.zshrc"
echo "✓ Then run: aider --mcp"
`,
    },
    steps: [
      {
        title: "Install Aider",
        body: "If not already installed:",
        code: "pip install aider-chat",
      },
      {
        title: "Set environment",
        body: "Add these to your shell profile:",
        code: `export MCP_SERVER_GAIA_MINT_COMMAND="npx"
export MCP_SERVER_GAIA_MINT_ARGS="-y,${MCP_PACKAGE}"`,
      },
      {
        title: "Run Aider with MCP",
        body: "",
        code: "aider --mcp",
      },
    ],
  },
];

export function downloadConfig(agent: AgentConfig) {
  const blob = new Blob([agent.configFile.content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = agent.configFile.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function copyToClipboard(text: string) {
  return navigator.clipboard?.writeText(text);
}
